import type { DirectoryUserRow } from '@ai-meeting/shared'

/**
 * How a chosen person is written into `value`.
 *
 * `email` is unambiguous and is what a field storing an address wants.
 * `name-and-email` keeps the name readable, for fields whose value is shown to
 * people — the action-item assignee ends up in the exported minutes, where
 * `thongdd@reactplus.com` reads worse than `Dang Dinh Thong`.
 */
export type DirectoryValueFormat = 'email' | 'name-and-email'

/** `<...>` around an address at the end of a value, as `name-and-email` writes it. */
const ANGLE_ADDRESS = /<([^<>]+)>\s*$/

/** @returns `value` as the given format writes it. */
export function formatDirectoryValue(
  user: DirectoryUserRow,
  format: DirectoryValueFormat,
): string {
  if (format === 'email') return user.email
  // A display name that is only the address would render as `a@b <a@b>`.
  return user.displayName === user.email
    ? user.email
    : `${user.displayName} <${user.email}>`
}

/**
 * The address inside a stored value, whichever format wrote it.
 *
 * Kept as the exact inverse of `formatDirectoryValue` so the two cannot drift:
 * everything that matches a stored value back to a person goes through here. A
 * value from before this picker existed — a bare name, typed by hand — has no
 * address in it and comes back unchanged, which matches nobody and leaves the
 * field in free text, exactly as it was.
 */
export function parseDirectoryEmail(value: string): string {
  const angled = ANGLE_ADDRESS.exec(value.trim())
  return (angled ? angled[1] : value).trim().toLowerCase()
}

/** A team in brackets closing a display name, e.g. `Trinh Hoang Giang (S1.AI)`. */
const TRAILING_TEAM = /\(([^()]+)\)\s*$/

/**
 * Split a stored value into the two lines a person is shown on (BRAI-575).
 *
 * The directory writes the team into the display name — `Thao Nguyen (S1.PO)` —
 * rather than carrying it as a field of its own, so the team has to come back
 * out of the name. When there is none the address takes the second line: it is
 * the half that matches a Backlog member, and the thing that tells two people
 * of the same name apart before a ticket is created under the wrong one.
 *
 * Lives here rather than beside the component that renders it so both halves
 * of the stored format — the address and the team — are read in one place.
 *
 * @param value The stored value, in whichever format wrote it.
 * @returns `name` always; `detail` is empty when the value carries neither.
 *
 * @example
 * splitAssignee('Thao Nguyen (S1.PO) <thao@x.com>') // { name: 'Thao Nguyen', detail: 'S1.PO' }
 * splitAssignee('Thao Nguyen <thao@x.com>')         // { name: 'Thao Nguyen', detail: 'thao@x.com' }
 */
export function splitAssignee(value: string): { name: string; detail: string } {
  const trimmed = value.trim()
  const angled = ANGLE_ADDRESS.exec(trimmed)
  const address = angled ? angled[1].trim() : ''
  const named = angled ? trimmed.slice(0, angled.index).trim() : trimmed

  const team = TRAILING_TEAM.exec(named)
  if (team) {
    return { name: named.slice(0, team.index).trim(), detail: team[1].trim() }
  }
  return { name: named, detail: address }
}
